import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Input, message, Spin } from "antd";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { fetchContacts } from "../store/slices/contactSlice";
import { contactFormAPI } from "../services/api";

const regions = [
  { id: "Americas", label: "Americas" },
  { id: "Europe", label: "Europe" },
  { id: "ANZ", label: "ANZ | Australia" },
  { id: "Asia_Pacific", label: "Asia Pacific" },
  { id: "Middle_East", label: "Middle East" },
  { id: "Africas", label: "Africa" },
];

const getCountryFlag = (country) => {
  const flagMap = {
    // Americas
    "USA": "/images/us.png",
    "Canada": "/images/cana.png",
    "Costa Rica": "/images/cr.png",
    "Colombia": "/images/co.png",
    "Mexico": "/images/mx.png",
    "Argentina": "/images/ar.png",
    // Europe
    "UK": "/images/gb.png",
    "Germany": "/images/de.png",
    "France": "/images/fr.png",
    "Netherlands": "/images/nl.png",
    "Belgium": "/images/be.png",
    "Sweden": "/images/se.png",
    "Poland": "/images/pl.png",
    "Hungary": "/images/hu.png",
    "Switzerland": "/images/ch.png",
    "Greece": "/images/gr.png",
    "Malta": "/images/mt.png",
    "Bulgaria": "/images/bg.png",
    "Croatia": "/images/hr.png",
    "Czech Republic": "/images/cz.png",
    "Denmark": "/images/dk.png",
    "Ireland": "/images/ie.png",
    "Portugal": "/images/pt.png",
    "Romania": "/images/ro.png",
    "Slovakia": "/images/sk.png",
    "Spain": "/images/es.png",
    "Serbia": "/images/rs.png",
    "Italy": "/images/it.png",
    "Macedonia": "/images/md.png",
    // ANZ
    "Australia": "/images/au.png",
    "New Zealand": "/images/nz.png",
    // Asia Pacific
    "India - Chennai": "/images/in.png",
    "India - Coimbatore": "/images/in.png",
    "India - Noida": "/images/in.png",
    "Singapore": "/images/sg.png",
    "Hong Kong": "/images/hk.png",
    "Malaysia": "/images/my.png",
    "Philippines": "/images/ph.png",
    "South Korea": "/images/kr.png",
    "Thailand": "/images/th.png",
    "Indonesia (West Indo)": "/images/id.png",
    "SriLanka": "/images/lk.png",
    "Vietnam": "/images/vn.png",
    "Azerbaijan": "/images/Azerbaijan.png",
    // Middle East
    "UAE": "/images/ae.png",
    "Turkey": "/images/tr.png",
    "Qatar": "/images/qa.png",
    "Saudi Arabia": "/images/saudiarabia.png",
    // Africa
    "South Africa": "/images/za.png",
    "Egypt": "/images/eg.png",
  };

  if (flagMap[country]) {
    return flagMap[country];
  }

  const countryKey = Object.keys(flagMap).find(key => {
    const keyBase = key.split(" - ")[0].toLowerCase();
    const countryBase = country.split(" - ")[0].toLowerCase();
    return countryBase.includes(keyBase) || keyBase.includes(countryBase);
  });

  return countryKey ? flagMap[countryKey] : null;
};

const groupContactsByRegion = (contacts) => {
  const grouped = {};
  contacts.forEach((contact) => {
    if (!grouped[contact.region]) {
      grouped[contact.region] = [];
    }
    grouped[contact.region].push(contact);
  });
  return grouped;
};

const fallbackContactData = {
  Americas: [
    {
      country: "USA",
      company: "Ampstek LLC",
      address: "103 Carnegie Center Drive Suite 300 Princeton-08540 USA",
      phone: "609-785-3232",
      email: "contact@ampstek.com",
    },
    {
      country: "Canada",
      company: "Ampstek IT Services Inc",
      address: "37 Kollar Drive Toronto-M1G 1E4 Canada",
      phone: "609-785-3232",
      email: "contact@ampstek.com",
    },
    {
      country: "Costa Rica",
      company: "Ampstek Costarica SRL",
      address: "800 Metros Sur Del Cruce Barrio La Abundancia San Carlos Alajuela-20101 Costa Rica",
      phone: "506 41021794",
      email: "contact@ampstek.com",
    },
    {
      country: "Colombia",
      company: "Ampstek Colombia SAS",
      address: "Cl. 146 #12-72, Usaquén, Bogotá, Colombia",
      email: "contact@ampstek.com",
    },
    {
      country: "Mexico",
      company: "Ampstek Technologies SC",
      address: "Blvd. Diaz Ordaz # 130, Col. Santa Maria Level 10 Monterrey, NL. CP 64650",
      email: "contact@ampstek.com",
    },
    {
      country: "Argentina",
      company: "Ampstek SAS",
      address: "Argentina, Ciudad De Buenos Aires, Ciudad De Buenos Aires Corrientes AV. 222 Piso PB 1043 Argentina",
      email: "contact@ampstek.com",
    },
  ],
  Europe: [
    {
      country: "UK",
      company: "Ampstek Services Limited",
      address: "124 City Road, London, England, EC1V 2NX",
      phone: "44 1339 891247",
      email: "contact@ampstek.com",
    },
    {
      country: "Germany",
      company: "Ampstek Deutschland GmbH",
      address: "Bethmannstr.8/ Berlinestr.51, 60311 Frankfurt am Main Deutschland Germany",
      email: "contact@ampstek.com",
    },
    {
      country: "France",
      company: "Ampstek France SAS",
      address: "250 B, BD Saint Germain 75007, Paris",
      email: "contact@ampstek.com",
    },
    {
      country: "Netherlands",
      company: "Ampstek Netherlands BV",
      address: "Zekeringstraat 17A,1014BM Amsterdam",
      email: "contact@ampstek.com",
    },
    {
      country: "Belgium",
      company: "Ampstek Belgium NV",
      address: "Boulevard Saint-Michel 65/6 - 1040 Brussels - Belgium",
      email: "contact@ampstek.com",
    },
    {
      country: "Sweden",
      company: "Ampstek Sweden AB",
      address: "Kungsbro Strand 29, 112 26 Stockholm",
      email: "contact@ampstek.com",
    },
    {
      country: "Poland",
      company: "Ampstek Poland Sp. z o.o.",
      address: "ul. Solec 81B lok. A51, 00-382 , Warsaw, Poland",
      phone: "48 22 1857586",
      email: "contact@ampstek.com",
    },
    {
      country: "Hungary",
      company: "Ampstek Hungary Kft",
      address: "1133 Budapest, Ipoly utca 20. üzlet 1.",
      email: "contact@ampstek.com",
    },
    {
      country: "Switzerland",
      company: "Ampstek Switzerland AG",
      address: "Bahnhofplatz 6300 Zug, Switzerland",
      email: "contact@ampstek.com",
    },
    {
      country: "Greece",
      company: "Ampstek Greece SA",
      address: "196, L Syngrou, Kalithea, 17671, GREECE",
      email: "contact@ampstek.com",
    },
    {
      country: "Malta",
      company: "Ampstek Malta Ltd",
      address: "Exchange Buildings, Republic Street Valletta, VLT 1117, Malta",
      email: "contact@ampstek.com",
    },
    {
      country: "Bulgaria",
      company: "Ampstek Bulgaria EOOD",
      address: "Rayko Daskalov St. 68, Floor 2, Office 8, 4000, Plovdiv, Bulgaria",
      email: "contact@ampstek.com",
    },
    {
      country: "Croatia",
      company: "Ampstek Croatia d.o.o.",
      address: "Hrusevecka Street 3, 10000 Zagreb, Croatia",
      email: "contact@ampstek.com",
    },
    {
      country: "Czech Republic",
      company: "Ampstek Czech Republic s.r.o.",
      address: "Zizkova 236/11, 470 01 Ceska Lipa",
      email: "contact@ampstek.com",
    },
    {
      country: "Denmark",
      company: "Ampstek Denmark A/S",
      address: "Advokatfirmaet Levin Skomagergade 8, 1. th., 4000 Roskilde Etterbeek",
      email: "contact@ampstek.com",
    },
    {
      country: "Ireland",
      company: "Ampstek Ireland Ltd",
      address: "The Black Church, Saint Mary's Place, D07 P4AX, Dublin, Ireland",
      email: "contact@ampstek.com",
    },
    {
      country: "Portugal",
      company: "Ampstek Portugal Lda",
      address: "Rua Joaquih Antonio de Aguiar No 43 RC Esq 1070 -150 Lisboa, Portugal.",
      email: "contact@ampstek.com",
    },
    {
      country: "Romania",
      company: "Ampstek Romania SRL",
      address: "Bucharest Sectorul 3, B-dul UNIRII,61, Bloc F3, Scara 4, Etaj 2, Apartment 208, Cod Postal 30828",
      phone: "40 609 785 3232",
      email: "contact@ampstek.com",
    },
    {
      country: "Slovakia",
      company: "Ampstek Slovakia s.r.o.",
      address: "Karpatská 18, 811 05 Bratislava - Old Town Slovakia",
      email: "contact@ampstek.com",
    },
    {
      country: "Spain",
      company: "Ampstek Spain SL",
      address: "AV Josep Tarradellas 38, Barcelona, Barcelona- 08029 Spain",
      email: "contact@ampstek.com",
    },
    {
      country: "Serbia",
      company: "Ampstek Serbia d.o.o.",
      address: "Belgrade, postal code: 11000,street Mirijevski Bulevar 54",
      email: "contact@ampstek.com",
    },
    {
      country: "Italy",
      company: "Ampstek Italy SRL",
      address: "Via del Lauro No. 9, Milan, c/o LEXIA",
      email: "contact@ampstek.com",
    },
    {
      country: "Macedonia",
      company: "Ampstek IT Services DOOEL Imporf-Export SKOPJE office",
      address: " No. 8/2-21 Skopje 1000, North Macedonia and registered under the laws of Macedonia.",
      email: "contact@ampstek.com",
    }
  ],
  ANZ: [
    {
      country: "Australia",
      company: "Ampstek Australia Pty Ltd",
      address: "Unit 16/28 Belmont Ave Rivervale, Western Australia. Australia 6103",
      phone: "61 862450617",
      email: "contact@ampstek.com",
    },
    {
      country: "New Zealand",
      company: "Ampstek New Zealand Ltd",
      address: "257A Queen Street Richmond-7020 New Zealand",
      phone: "64 (9) 8868295",
      email: "contact@ampstek.com",
    },
  ],
  Asia_Pacific: [
    {
      country: "India - Chennai",
      company: "Ampstek India Pte Ltd",
      address: "1st Floor, Unit No: 1 & 2, TEK TOWER, No.11, Old Mahabalipuram Road, Okkiyam, Thuraipakkam, Chennai-600 096",
      email: "contact@ampstek.com",
    },
    {
      country: "India - Coimbatore",
      company: "AmpsTek Pvt Ltd",
      address: "75/771, 1st Floor, 7th Street, Tatabad, Coimbatore - 641012",
      email: "contact@ampstek.com",
    },
    {
      country: "India - Noida",
      company: "Ampstek Solution India Private Limited",
      address: "First Floor, Unit No. 87, Block H, Noida Gautam Buddh Nagar, Noida 201201, Uttar Pradesh UP, India",
      email: "contact@ampstek.com",
    },
    {
      country: "Singapore",
      company: "Ampstek Singapore Pte Ltd",
      address: "34-00 3 Temasek Avenue Centennial Towers Singapore-039190",
      phone: "65 69160136",
      email: "contact@ampstek.com",
    },
    {
      country: "Hong Kong",
      company: "Ampstek Hong Kong Pte Ltd",
      address: "7-9 Austin Ave, Office Room Number 77, Hong Kong",
      email: "contact@ampstek.com",
    },
    {
      country: "Malaysia",
      company: "Ampstek Malaysia Sdn Bhd",
      address: "32A Lorong Batu Nilam 131 Bukit Tinggi Klang-41200 Malaysia",
      email: "contact@ampstek.com",
    },
    {
      country: "Philippines",
      company: "Ampstek Philippines Inc",
      address: "3rd Floor 170 Salcedo Street One Salcedo Center Legaspi Village Metro Manila Makati-1223 Philippines",
      phone: "63 977 461 6676",
      email: "contact@ampstek.com",
    },
    {
      country: "South Korea",
      company: "Ampstek South Korea Co Ltd",
      address: "1374, 369, Gangnam-Daero, Seocho-Gu, Seoul, Republic Of Korea",
      email: "contact@ampstek.com",
    },
    {
      country: "Thailand",
      company: "Ampstek Thailand Ltd",
      address: "68/47 Y Residence Sukhumvit 113, Moo 3, Soi Dansamrong 42/1, Samrong Nuea Sub-District Mueang Samut Prakan District, Samut Prakan",
      email: "contact@ampstek.com",
    },
    {
      country: "Indonesia (West Indo)",
      company: "Cv Westindo Indonesia",
      address: "B10 Block B8 Perumahan Gading Kencana Puri Gading Jimbaran Kec Kuta Sel Kabupaten Badung Bali-80361 Indonesia",
      email: "contact@ampstek.com",
    },
    {
      country: "SriLanka",
      company: "Ampstek Lanka Pvt Ltd",
      address: "No: 57, W. A. Sillva Mawatha, Colombo-06, Postcode: 00600",
      email: "contact@ampstek.com",
    },
    {
      country: "Vietnam",
      company: "Ampstek Vietnam Co Ltd",
      address: "5F&9F, CJ Building, No 2Bis-4-6 Le Thanh Ton Street Ben Nghe Ward, District 1, Ho Chi Minh City, Vietnam",
      phone: "84 969 999 125",
      email: "contact@ampstek.com",
    }
  ],
  Middle_East: [
    {
      country: "UAE",
      company: "Ampstek MEA FZCO",
      address: "Dubai Silicon, Oasis, DDP, Building A2, Unit, 101, Dubai, UAE",
      email: "contact@ampstek.com",
    },
    {
      country: "Turkey",
      company: "Ampstek IT Servis Bilisim Limited Sirketi",
      address: "61/2 Sultan Selim Meh Eski Buykdere Caddesi No 61 2 Kagithane Istanbul",
      email: "contact@ampstek.com",
    },
    {
      country: "Qatar",
      company: "Ampstek Technologies Services",
      address: "Building No 21 Zone 18 Street 977 Doha Qatar",
      email: "contact@ampstek.com",
    },
    ,
    {
      country: "Saudi Arabia",
      company: "Ampstek Technology Services",
      address: "No:28,first floor,Raden commercial center,AI muruj,olaya street,riyadh,Saudi Arabia",
      email: "contact@ampstek.com",
    }
  ],
  Africas: [
    {
      country: "South Africa",
      company: "Ampstek South Africa (Pty) Ltd",
      address: "147 Cedar Hills Estate, Fourways, Johannesburg, Gauteng-2512",
      email: "contact@ampstek.com",
    },
    {
      country: "Egypt",
      company: "Ampstek IT Services",
      address: "Room 3, Apartment 805, 8th Floor, Al Maamora Towers, Tower C, 7th District, Nasr City, Cairo",
      email: "contact@ampstek.com",
    }
  ],
};

export default function ContactUs() {
  const dispatch = useDispatch();
  const { items: contacts, loading, error } = useSelector((state) => state.contacts);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRegion, setSelectedRegion] = useState("Americas");
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    dispatch(fetchContacts({ is_active: true }));
  }, [dispatch]);

  useEffect(() => {
    const regionParam = searchParams.get("global");
    if (regionParam) {
      const region = regions.find((r) => r.id === regionParam);
      if (region) {
        setSelectedRegion(region.id);
      }
    }
  }, [searchParams]);

  const handleRegionChange = (regionId) => {
    setSelectedRegion(regionId);
    setSearchParams({ global: regionId });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleFormSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      
      const response = await contactFormAPI.submit({
        firstname: values.firstname,
        lastname: values.lastname || null,
        email: values.email,
        subject: values.subject || null,
        requirement: values.requirement || null
      });

      if (response.data.success) {
        messageApi.success(response.data.message || "Thanks for reaching out! Our team will get back to you shortly.");
        form.resetFields();
      } else {
        throw new Error(response.data.message || "Failed to submit form");
      }
    } catch (error) {
      console.error("Contact form submission error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Something went wrong. Please try again.";
      messageApi.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group contacts by region from Redux store, fallback to hardcoded data
  const contactDataFromStore = contacts.length > 0 
    ? groupContactsByRegion(contacts.filter(c => c.is_active === 1))
    : fallbackContactData;
  
  const currentContacts = contactDataFromStore[selectedRegion] || [];

  return (
    <div className="contact-us-page" id="contact-us">
      {contextHolder}
      <section className="global-presence-section py-24 px-4">
        <div className="container">
          <div className="text-center mb-8">
            <h2 className="global-presence-title">Our Global Presence</h2>
            <p className="global-presence-subtitle">Explore our services in specific geography</p>
          </div>

          <div className="region-tabs-wrapper mb-8">
            <div className="region-tabs">
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() => handleRegionChange(region.id)}
                  className={`region-tab ${selectedRegion === region.id ? "region-tab--active" : ""}`}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : (
            <div className="contact-cards-grid">
              {currentContacts.length > 0 ? (
                currentContacts.map((contact, index) => {
                  // Helper function to get the correct flag image URL
                  const getFlagImageUrl = (flagPath) => {
                    if (!flagPath) return null;
                    
                    // If it's an uploaded image (starts with /uploads), prepend backend URL
                    if (flagPath.startsWith('/uploads')) {
                      const backendUrl = process.env.REACT_APP_ADMIN_API_URL || 'https://ampsweb.saassrs.com';
                      return `${backendUrl}${flagPath}`;
                    }
                    
                    // Otherwise, it's a static image from public folder
                    return flagPath;
                  };

                  const flagPath = contact.country_flag || getCountryFlag(contact.country);
                  const flagImageUrl = getFlagImageUrl(flagPath);
                  
                  return (
                    <div key={contact.id || index} className="contact-card">
                      <div className="contact-card__header">
                        {flagImageUrl && (
                          <div className="contact-card__flag">
                            <img
                              src={flagImageUrl}
                              alt={`${contact.country} flag`}
                              onError={(e) => {
                                // Fallback to static flag if uploaded image fails to load
                                const fallbackFlag = getCountryFlag(contact.country);
                                if (fallbackFlag && e.target.src !== fallbackFlag) {
                                  e.target.src = fallbackFlag;
                                } else {
                                  e.target.style.display = 'none';
                                }
                              }}
                            />
                          </div>
                        )}
                        <h3 className="contact-card__country">{contact.country}</h3>
                      </div>
                      <p className="contact-card__company">{contact.company}</p>
                      <p className="contact-card__address">{contact.address}</p>
                      {contact.phone && (
                        <div className="contact-card__info">
                          <PhoneOutlined className="contact-card__icon" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                      <div className="contact-card__info">
                        <MailOutlined className="contact-card__icon" />
                        <a href={`mailto:${contact.email}`} className="contact-card__email">
                          {contact.email}
                        </a>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                  No contacts found for this region.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="get-in-touch-section py-24 px-4">
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-6 mb-5 mb-lg-0">
              <h2 className="get-in-touch-title">Get In Touch</h2>
              <p className="get-in-touch-description">
                If you need more information or have any inquiries, don't hesitate to reach out! Our committed team is ready to assist you with prompt and thorough responses.
              </p>
              <div className="get-in-touch-illustration">
                <img src="/images/contactimg2.jpg" alt="Contact Us" className="img-fluid" />
              </div>
            </div>
            <div className="col-12 col-lg-6">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleFormSubmit}
                className="contact-form-main"
              >
                <div className="row">
                  <div className="col-12 col-md-6">
                    <Form.Item
                      name="firstname"
                      label="Firstname"
                      rules={[{ required: true, message: "Please enter your first name" }]}
                    >
                      <Input size="large" placeholder="Firstname *" className="contact-form-input" />
                    </Form.Item>
                  </div>
                  <div className="col-12 col-md-6">
                    <Form.Item name="lastname" label="Lastname">
                      <Input size="large" placeholder="Lastname" className="contact-form-input" />
                    </Form.Item>
                  </div>
                </div>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                >
                  <Input size="large" placeholder="Email *" className="contact-form-input" />
                </Form.Item>
                <Form.Item name="subject" label="Subject">
                  <Input size="large" placeholder="Subject" className="contact-form-input" />
                </Form.Item>
                <Form.Item name="requirement" label="Describe your requirement">
                  <Input.TextArea
                    rows={6}
                    placeholder="Describe your requirement"
                    className="contact-form-textarea"
                  />
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    className="contact-form-submit"
                    loading={isSubmitting}
                  >
                    Submit
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
